import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import ExcelJS from 'exceljs';

import logoISC from '../components/images//logo.png';
import logoSEP from '../components/images//SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

const TRIMESTRES = [
	{ valor: '1', etiqueta: '1° Trimestre' },
	{ valor: '2', etiqueta: '2° Trimestre' },
	{ valor: '3', etiqueta: '3° Trimestre' }
];

const CATEGORY_STYLES = {
	Lenguajes: { header: '#dce8bd', cell: '#f2f7dd' },
	'Saberes y Pens. Cient.': { header: '#cfe3ab', cell: '#e8f3cc' },
	'Est. Nat. y Soc.': { header: '#f5d9d7', cell: '#fce7e5' },
	'Et. Nat. Soc.': { header: '#f5d9d7', cell: '#fce7e5' },
	'De lo Hum y lo Com.': { header: '#f8cddf', cell: '#fde1eb' },
	'De lo Hum y lo Com': { header: '#f8cddf', cell: '#fde1eb' }
};

const SPECIAL_COLUMN_STYLES = {
	No: { header: '#f7f0b2', cell: '#fff8d7' },
	Nombre: { header: '#f7f0b2', cell: '#fff8d7' },
	PromedioGeneral: { header: '#dcd4f7', cell: '#eee9ff' },
	Asistencias: { header: '#dcd4f7', cell: '#f1edff' },
	Inasistencias: { header: '#dcd4f7', cell: '#f1edff' },
	Observaciones: { header: '#e6e6e6', cell: '#f5f5f5' },
	Sugerencias: { header: '#e6e6e6', cell: '#f5f5f5' }
};

const normalizarTexto = (texto = '') =>
	texto
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.replace(/[^A-Z0-9\s]/gi, '')
		.toUpperCase()
		.trim();

const CONECTORES_APELLIDO = new Set(
	[
		'DE',
		'DEL',
		'DE LA',
		'DE LAS',
		'DE LOS',
		'LA',
		'LAS',
		'LOS',
		'Y',
		'MC',
		'MAC',
		'VAN',
		'VON',
		'VON DER',
		'SAN',
		'SANTA',
		'DA',
		'DAS',
		'DI',
		'DU',
		'DES'
	]
		.map((token) => token.split(/\s+/))
		.flat()
		.map((token) => normalizarTexto(token))
		.filter((token) => token.length > 0)
);

const NOMBRES_COMUNES = new Set(
	[
		'AARON',
		'ABIGAIL',
		'ALEJANDRA',
		'ALEJANDRO',
		'ALFONSO',
		'ALICIA',
		'ALMA',
		'AMERICA',
		'ANDREA',
		'ANGEL',
		'ANGELA',
		'ANGELICA',
		'ANTONIO',
		'ARACELI',
		'ARMANDO',
		'BARBARA',
		'CARLA',
		'CARLOS',
		'CAROLINA',
		'CRISTIAN',
		'DAVID',
		'DEYANIRA',
		'DIEGO',
		'EDGAR',
		'EDUARDO',
		'ELENA',
		'ELISA',
		'ELIZABETH',
		'EMILIANO',
		'ENRIQUE',
		'ERIKA',
		'ESMERALDA',
		'ESTEBAN',
		'FERNANDA',
		'FERNANDO',
		'FRANCISCO',
		'GABRIEL',
		'GABRIELA',
		'GEORGINA',
		'GERARDO',
		'GLORIA',
		'GUADALUPE',
		'HECTOR',
		'IVAN',
		'JAVIER',
		'JESUS',
		'JIMENA',
		'JORGE',
		'JOSE',
		'JOSEFINA',
		'JUAN',
		'JULIA',
		'JULIETA',
		'KAREN',
		'KARLA',
		'LAURA',
		'LEONARDO',
		'LESLEY',
		'LUIS',
		'MANUEL',
		'MARIA',
		'MARIANA',
		'MARIO',
		'MARISOL',
		'MAURICIO',
		'MIGUEL',
		'MIRANDA',
		'MONICA',
		'NATALIA',
		'NICOLAS',
		'NOEMI',
		'PABLO',
		'PATRICIA',
		'PAULA',
		'PAULINA',
		'PEDRO',
		'REBECA',
		'RICARDO',
		'ROBERTO',
		'RODRIGO',
		'ROSA',
		'SAMANTHA',
		'SANDRA',
		'SANTIAGO',
		'SERGIO',
		'SOFIA',
		'URIEL',
		'VALENTINA',
		'VALERIA',
		'VICTOR',
		'XIMENA',
		'YARETZI',
		'YESSICA'
	].map((nombre) => normalizarTexto(nombre))
);

const esConector = (palabra = '') => CONECTORES_APELLIDO.has(normalizarTexto(palabra));

const unirTokens = (tokens = []) => tokens.filter(Boolean).join(' ').trim();

const tomarApellidoDesdeInicio = (partes, inicio, minimoRestantes) => {
	const tokens = [];
	let idx = inicio;
	while (idx < partes.length) {
		const token = partes[idx];
		tokens.push(token);
		idx += 1;
		const restantes = partes.length - idx;
		if (restantes < minimoRestantes) {
			break;
		}
		if (!esConector(token)) {
			const siguiente = partes[idx];
			if (!esConector(siguiente)) {
				break;
			}
		}
	}
	return { tokens, nextIndex: idx };
};

const crearDescomposicion = (nombresTokens, paternoTokens, maternoTokens) => ({
	nombres: unirTokens(nombresTokens),
	apellidoPaterno: unirTokens(paternoTokens),
	apellidoMaterno: unirTokens(maternoTokens),
	_tokens: {
		nombres: nombresTokens,
		apellidoPaterno: paternoTokens,
		apellidoMaterno: maternoTokens
	}
});

const descomponerApellidosPrimero = (partes) => {
	const { tokens: paternoTokens, nextIndex } = tomarApellidoDesdeInicio(partes, 0, 2);
	const { tokens: maternoTokens, nextIndex: idxFinal } = tomarApellidoDesdeInicio(partes, nextIndex, 1);
	const nombresTokens = partes.slice(idxFinal);
	return crearDescomposicion(nombresTokens, paternoTokens, maternoTokens);
};

const descomponerNombresPrimero = (partes) => {
	let idxMaterno = partes.length - 1;
	const maternoTokens = [];
	while (idxMaterno >= 0) {
		const token = partes[idxMaterno];
		maternoTokens.unshift(token);
		idxMaterno -= 1;
		const restantes = idxMaterno + 1;
		if (restantes < 1) {
			break;
		}
		if (!esConector(token)) {
			const anterior = partes[idxMaterno];
			if (!esConector(anterior)) {
				break;
			}
		}
	}

	let idxPaterno = idxMaterno;
	const paternoTokens = [];
	while (idxPaterno >= 0) {
		const token = partes[idxPaterno];
		paternoTokens.unshift(token);
		idxPaterno -= 1;
		const restantes = idxPaterno + 1;
		if (restantes < 1) {
			break;
		}
		if (!esConector(token)) {
			const anterior = partes[idxPaterno];
			if (!esConector(anterior)) {
				break;
			}
		}
	}

	const nombresTokens = partes.slice(0, idxPaterno + 1);
	return crearDescomposicion(nombresTokens, paternoTokens, maternoTokens);
};

const contarTokensValidos = (tokens = []) => tokens.filter((token) => token && !esConector(token)).length;

const puntuarDescomposicion = (descomposicion, partes) => {
	const { nombres: nombresTexto, apellidoPaterno, apellidoMaterno, _tokens } = descomposicion;
	const nombresTokens = _tokens?.nombres ?? (nombresTexto ? nombresTexto.split(/\s+/) : []);
	const paternoTokens = _tokens?.apellidoPaterno ?? (apellidoPaterno ? apellidoPaterno.split(/\s+/) : []);
	const maternoTokens = _tokens?.apellidoMaterno ?? (apellidoMaterno ? apellidoMaterno.split(/\s+/) : []);

	const scoreApPaterno = contarTokensValidos(paternoTokens);
	const scoreApMaterno = contarTokensValidos(maternoTokens);
	const scoreNombres = contarTokensValidos(nombresTokens);
	const scoreNombresComunes = nombresTokens.filter((token) => NOMBRES_COMUNES.has(normalizarTexto(token))).length;

	const primerTokenOriginal = normalizarTexto(partes[0] || '');
	const ultimoTokenOriginal = normalizarTexto(partes[partes.length - 1] || '');
	const primerTokenPaterno = normalizarTexto(paternoTokens[0] || '');
	const ultimoTokenMaterno = normalizarTexto(maternoTokens[maternoTokens.length - 1] || '');

	const coincidenciaExtremos = (primerTokenPaterno === primerTokenOriginal ? 1 : 0) +
		(ultimoTokenMaterno === ultimoTokenOriginal ? 1 : 0);

	return (
		scoreApPaterno * 1000 +
		scoreApMaterno * 100 +
		scoreNombresComunes * 10 +
		scoreNombres +
		coincidenciaExtremos
	);
};

const descomponerNombre = (nombreCompleto = '') => {
	const partes = nombreCompleto.trim().split(/\s+/).filter(Boolean);
	if (partes.length === 0) {
		return { nombres: '', apellidoPaterno: '', apellidoMaterno: '' };
	}
	if (partes.length === 1) {
		return { nombres: partes[0], apellidoPaterno: '', apellidoMaterno: '' };
	}
	if (partes.length === 2) {
		return { nombres: partes[1], apellidoPaterno: partes[0], apellidoMaterno: '' };
	}

	const opcionApellidosPrimero = descomponerApellidosPrimero(partes);
	const opcionNombresPrimero = descomponerNombresPrimero(partes);
	const puntuacionApellidos = puntuarDescomposicion(opcionApellidosPrimero, partes);
	const puntuacionNombres = puntuarDescomposicion(opcionNombresPrimero, partes);

	const mejorOpcion = puntuacionNombres > puntuacionApellidos ? opcionNombresPrimero : opcionApellidosPrimero;

	return {
		nombres: mejorOpcion.nombres,
		apellidoPaterno: mejorOpcion.apellidoPaterno,
		apellidoMaterno: mejorOpcion.apellidoMaterno
	};
};

const construirNombreReferencia = (alumno = {}, data = {}, fallback = '') => {
	const candidatos = [
		alumno?.NombreCompleto,
		[alumno?.ApellidoPaterno, alumno?.ApellidoMaterno, alumno?.Nombre]
			.filter(Boolean)
			.join(' '),
		data?.alumno?.nombreCompleto,
		[data?.alumno?.apellidoPaterno, data?.alumno?.apellidoMaterno, data?.alumno?.nombre]
			.filter(Boolean)
			.join(' '),
		data?.alumno?.nombre,
		fallback
	];

	for (const candidato of candidatos) {
		if (typeof candidato === 'string' && candidato.trim().length > 0) {
			return candidato.trim();
		}
	}

	return '';
};

const construirClaveOrden = (alumno, data, nombreFallback = '') => {
	const nombreReferencia = construirNombreReferencia(alumno, data, nombreFallback);
	const descompuesto = descomponerNombre(nombreReferencia);
	return [
		normalizarTexto(descompuesto.apellidoPaterno),
		normalizarTexto(descompuesto.apellidoMaterno),
		normalizarTexto(descompuesto.nombres)
	].join('|');
};

const limpiarNombreParaMostrar = (nombre = '') =>
	nombre
		.toString()
		.trim()
		.replace(/\s+/g, ' ')
		.toUpperCase();

const getCategoryStyle = (nombreCategoria = '', tipo) => {
	const entrada = Object.entries(CATEGORY_STYLES).find(
		([clave]) => clave.toLowerCase() === nombreCategoria.toLowerCase()
	);
	const estilos = entrada ? entrada[1] : { header: '#e1e1e1', cell: '#f7f7f7' };
	return estilos[tipo];
};

const getSpecialStyle = (clave, tipo) => SPECIAL_COLUMN_STYLES[clave]?.[tipo];

const hexToExcelArgb = (hex = '#FFFFFF') => {
	if (!hex) {
		return 'FFFFFFFF';
	}
	const limpio = hex.replace('#', '').trim();
	const expandido = limpio.length === 3 ? limpio.split('').map((c) => c + c).join('') : limpio.padStart(6, '0');
	return `FF${expandido.toUpperCase()}`;
};

const crearRelleno = (hex) => ({
	type: 'pattern',
	pattern: 'solid',
	fgColor: { argb: hexToExcelArgb(hex) }
});

const BORDE_DELGADO = {
	top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
	left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
	bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
	right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
};

const ConsultarGruposCalificaciones = () => {
	const [grupos, setGrupos] = useState([]);
	const [grupoSeleccionado, setGrupoSeleccionado] = useState('');
	const [trimestreSeleccionado, setTrimestreSeleccionado] = useState('1');

	const [datosCrudos, setDatosCrudos] = useState([]);
	const [loadingGrupos, setLoadingGrupos] = useState(false);
	const [loadingTabla, setLoadingTabla] = useState(false);
	const [error, setError] = useState('');

	const apiBases = useMemo(() => resolveApiBaseList(), []);

	const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
		let lastError = null;

		for (const base of apiBases) {
			const normalizedBase = base.replace(/\/$/, '');
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			const url = `${normalizedBase}${normalizedPath}`;

			try {
				const response = await fetch(url, options);
				const text = await response.text();

				if (!response.ok) {
					let message = text || `Error ${response.status}`;
					try {
						const parsed = text ? JSON.parse(text) : null;
						if (parsed && typeof parsed === 'object') {
							message = parsed.message || parsed.error || message;
						}
					} catch {
						// mantener mensaje original
					}

					const errorObj = new Error(message);
					errorObj.status = response.status;
					lastError = errorObj;
					console.warn(`Warning: petición fallida a ${url}:`, errorObj);
					continue;
				}

				const trimmed = text.trim();
				if (!trimmed) {
					return { data: {}, baseUrl: normalizedBase };
				}

				try {
					return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
				} catch (parseErr) {
					lastError = new Error(`Respuesta inválida desde ${url}`);
					console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
				}
			} catch (error) {
				lastError = error;
				console.warn(`Warning: error de red al solicitar ${url}:`, error);
			}
		}

		throw lastError || new Error('No se pudo contactar al servidor.');
	}, [apiBases]);

	const cargarGrupos = useCallback(async () => {
		try {
			setLoadingGrupos(true);
			setError('');

			const { data } = await fetchJsonWithFallback('/grupos/activos');
			setGrupos(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error('❌ Error al cargar grupos activos:', err);
			setError(err.message || 'Error al cargar los grupos activos.');
			setGrupos([]);
		} finally {
			setLoadingGrupos(false);
		}
	}, [fetchJsonWithFallback]);

	useEffect(() => {
		cargarGrupos();
	}, [cargarGrupos]);

	const cargarCalificacionesGrupo = useCallback(async (grupoId) => {
		if (!grupoId) {
			setDatosCrudos([]);
			return;
		}

		try {
			setLoadingTabla(true);
			setError('');

			const { data: alumnos } = await fetchJsonWithFallback(`/grupos/${grupoId}/alumnos-detalle`);
			const alumnosValidos = Array.isArray(alumnos) ? alumnos : [];

			if (alumnosValidos.length === 0) {
				setDatosCrudos([]);
				return;
			}

			const resultados = await Promise.all(
				alumnosValidos.map(async (alumno) => {
					try {
						const { data } = await fetchJsonWithFallback(`/calificaciones/alumno/${alumno.IdUsuario}`);
						return { alumno, data };
					} catch (err) {
						console.error(`❌ Error al cargar calificaciones del alumno ${alumno.NombreCompleto}:`, err);
						return { alumno, data: null, error: err.message };
					}
				})
			);

			setDatosCrudos(resultados);
		} catch (err) {
			console.error('❌ Error al cargar calificaciones del grupo:', err);
			setError(err.message || 'Error al cargar las calificaciones del grupo.');
			setDatosCrudos([]);
		} finally {
			setLoadingTabla(false);
		}
	}, [fetchJsonWithFallback]);

	const manejarCambioGrupo = async (event) => {
		const value = event.target.value;
		setGrupoSeleccionado(value);
		await cargarCalificacionesGrupo(value);
	};

	const manejarCambioTrimestre = (event) => {
		setTrimestreSeleccionado(event.target.value);
	};

	const descripcionGrupoSeleccionado = useMemo(() => {
		if (!grupoSeleccionado) return '';
		const grupo = grupos.find((g) => String(g.IdGrupo) === String(grupoSeleccionado));
		if (!grupo) return '';
		const grado = grupo.Grado ? `${grupo.Grado}°` : '';
		const nombre = grupo.Grupo || grupo.NombreGrupo || grupo.Nombre || grupo.Letra || '';
		const ciclo = grupo.CicloEscolar ? ` - ${grupo.CicloEscolar}` : '';
		const literal = [grado, nombre].filter(Boolean).join(' ').trim();
		return (literal || `Grupo ${grupo.IdGrupo}`) + ciclo;
	}, [grupos, grupoSeleccionado]);

	const etiquetaTrimestreSeleccionado = useMemo(() => {
		return TRIMESTRES.find((t) => t.valor === trimestreSeleccionado)?.etiqueta || '';
	}, [trimestreSeleccionado]);

	const { filas, categorias } = useMemo(() => {
		const categoriasMapa = new Map();

		const filasProcesadas = datosCrudos.map((entrada) => {
			const { alumno, data } = entrada;

			const resultado = {
				id: alumno?.IdAlumno ?? alumno?.IdUsuario ?? null,
				nombre: alumno?.NombreCompleto ?? '—',
				calificaciones: {},
				promediosCategoria: {},
				promedioGeneral: '-',
				asistencias: '-',
				inasistencias: '-',
				observaciones: '',
				sugerencias: ''
			};

			const nombreReferencia = construirNombreReferencia(alumno, data, resultado.nombre);
			resultado.nombre = limpiarNombreParaMostrar(nombreReferencia || resultado.nombre);
			resultado._sortKey = construirClaveOrden(alumno, data, nombreReferencia || resultado.nombre);

			if (!data || !data.categorias) {
				return resultado;
			}

			const trimestre = trimestreSeleccionado;

			Object.values(data.categorias || {}).forEach((categoria) => {
				const nombreCategoria = categoria?.nombre || 'Sin categoría';
				if (!categoriasMapa.has(nombreCategoria)) {
					categoriasMapa.set(nombreCategoria, new Set());
				}

				if (!resultado.calificaciones[nombreCategoria]) {
					resultado.calificaciones[nombreCategoria] = {};
				}

				Object.entries(categoria?.materias || {}).forEach(([nombreMateria, infoMateria]) => {
					categoriasMapa.get(nombreCategoria).add(nombreMateria);
					const calificacion = infoMateria?.calificaciones?.[trimestre]?.calificacion;
					resultado.calificaciones[nombreCategoria][nombreMateria] = calificacion ?? '';
				});

				const valoresCategoria = Object.values(resultado.calificaciones[nombreCategoria])
					.map((valor) => parseFloat(valor))
					.filter((valor) => !Number.isNaN(valor));

				if (valoresCategoria.length > 0) {
					const suma = valoresCategoria.reduce((acc, valor) => acc + valor, 0);
					resultado.promediosCategoria[nombreCategoria] = (suma / valoresCategoria.length).toFixed(1);
				} else {
					resultado.promediosCategoria[nombreCategoria] = '';
				}
			});

			const promedioGeneral = data?.promedios?.porTrimestre?.[trimestre];
			resultado.promedioGeneral =
				promedioGeneral !== undefined && promedioGeneral !== null ? promedioGeneral : '-';

			resultado.asistencias =
				data?.resumenAsistencias?.porTrimestre?.[trimestre]?.asistencias ?? '-';
			resultado.inasistencias =
				data?.resumenAsistencias?.porTrimestre?.[trimestre]?.inasistencias ?? '-';

			resultado.observaciones =
				data?.observacionesGenerales?.[trimestre]?.trim?.() ||
				(typeof data?.observacionesGenerales?.[trimestre] === 'string'
					? data.observacionesGenerales[trimestre]
					: '');

			resultado.sugerencias =
				data?.sugerenciasGenerales?.[trimestre]?.trim?.() ||
				(typeof data?.sugerenciasGenerales?.[trimestre] === 'string'
					? data.sugerenciasGenerales[trimestre]
					: '');

			return resultado;
		});

		filasProcesadas.sort((a, b) => a._sortKey.localeCompare(b._sortKey));
		filasProcesadas.forEach((fila) => {
			delete fila._sortKey;
		});

		const categoriasOrdenadas = Array.from(categoriasMapa.entries())
			.map(([nombre, materiasSet]) => ({
				nombre,
				materias: Array.from(materiasSet).sort((a, b) => a.localeCompare(b))
			}))
			.sort((a, b) => a.nombre.localeCompare(b.nombre));

		return { filas: filasProcesadas, categorias: categoriasOrdenadas };
	}, [datosCrudos, trimestreSeleccionado]);

	const exportarExcel = useCallback(async () => {
		if (!filas.length) {
			alert('No hay datos para exportar.');
			return;
		}

		try {
			const workbook = new ExcelJS.Workbook();
			workbook.creator = 'Instituto Silviano Carrillo';
			workbook.created = new Date();
			const worksheet = workbook.addWorksheet('Calificaciones Grupo', {
				properties: { defaultRowHeight: 22 },
				views: [{ state: 'frozen', xSplit: 2, ySplit: 2 }]
			});

			const headerFila1 = [];
			const headerFila2 = [];
			const columnasMeta = [];
			const mergesEspeciales = [];
			const mergesCategorias = [];

			let indiceColumna = 1;
			const agregarColumnaEspecial = (clave, titulo, ancho) => {
				headerFila1.push(titulo);
				headerFila2.push('');
				columnasMeta.push({ tipo: 'especial', clave, ancho });
				mergesEspeciales.push(indiceColumna);
				indiceColumna += 1;
			};

			agregarColumnaEspecial('No', 'No', 6);
			agregarColumnaEspecial('Nombre', 'Nombre del alumno', 38);

			categorias.forEach((categoria) => {
				const materias = categoria.materias;
				const columnasCategoria = materias.length + 1;
				const columnaInicial = indiceColumna;

				materias.forEach((materia, idx) => {
					headerFila1.push(idx === 0 ? categoria.nombre : '');
					headerFila2.push(materia);
					columnasMeta.push({
						tipo: 'categoria',
						categoria: categoria.nombre,
						materia,
						promedio: false,
						ancho: 18
					});
					indiceColumna += 1;
				});

				headerFila1.push('');
				headerFila2.push('Promedio');
				columnasMeta.push({
					tipo: 'categoria',
					categoria: categoria.nombre,
					materia: null,
					promedio: true,
					ancho: 16
				});
				indiceColumna += 1;

				mergesCategorias.push({ desde: columnaInicial, hasta: columnaInicial + columnasCategoria - 1, nombre: categoria.nombre });
			});

			agregarColumnaEspecial('PromedioGeneral', 'Promedio General', 18);
			agregarColumnaEspecial('Asistencias', 'Asistencias', 14);
			agregarColumnaEspecial('Inasistencias', 'Inasistencias', 14);
			agregarColumnaEspecial('Observaciones', 'Observaciones', 40);
			agregarColumnaEspecial('Sugerencias', 'Sugerencias', 40);

			worksheet.addRow(headerFila1);
			worksheet.addRow(headerFila2);

			mergesEspeciales.forEach((col) => {
				worksheet.mergeCells(1, col, 2, col);
			});
			mergesCategorias.forEach(({ desde, hasta }) => {
				worksheet.mergeCells(1, desde, 1, hasta);
			});

			worksheet.columns = columnasMeta.map((meta) => ({ width: meta.ancho || (meta.tipo === 'categoria' ? 18 : 12) }));

			const filaEncabezado1 = worksheet.getRow(1);
			const filaEncabezado2 = worksheet.getRow(2);
			filaEncabezado1.height = 26;
			filaEncabezado2.height = 26;

			const aplicarBorde = (celda) => {
				celda.border = BORDE_DELGADO;
			};

			columnasMeta.forEach((meta, idx) => {
				const columnaExcel = idx + 1;
				const celdaSuperior = filaEncabezado1.getCell(columnaExcel);
				const celdaInferior = filaEncabezado2.getCell(columnaExcel);

				if (meta.tipo === 'especial') {
					const colorHeader = getSpecialStyle(meta.clave, 'header');
					const colorCell = getSpecialStyle(meta.clave, 'cell');
					if (colorHeader) {
						celdaSuperior.fill = crearRelleno(colorHeader);
					}
					if (colorCell) {
						celdaInferior.fill = crearRelleno(colorCell);
					}
					celdaSuperior.font = { bold: true, color: { argb: 'FF000000' } };
					celdaInferior.font = { bold: true, color: { argb: 'FF000000' } };
					celdaSuperior.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
					celdaInferior.alignment = meta.clave === 'Nombre'
						? { horizontal: 'center', vertical: 'middle', wrapText: true }
						: { horizontal: 'center', vertical: 'middle', wrapText: true };
				} else {
					const colorHeader = getCategoryStyle(meta.categoria, 'header');
					const colorCell = getCategoryStyle(meta.categoria, 'cell');
					if (colorHeader && celdaSuperior.value) {
						celdaSuperior.fill = crearRelleno(colorHeader);
					}
					if (colorCell) {
						celdaInferior.fill = crearRelleno(colorCell);
					}
					celdaInferior.font = { bold: true, color: { argb: 'FF000000' } };
					celdaSuperior.font = { bold: true, color: { argb: 'FF000000' } };
					celdaSuperior.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
					celdaInferior.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
				}
				aplicarBorde(celdaSuperior);
				aplicarBorde(celdaInferior);
			});

			filas.forEach((fila, idx) => {
				const valores = columnasMeta.map((meta) => {
					if (meta.tipo === 'especial') {
						switch (meta.clave) {
							case 'No':
								return idx + 1;
							case 'Nombre':
								return fila.nombre;
							case 'PromedioGeneral':
								return typeof fila.promedioGeneral === 'number'
									? fila.promedioGeneral
									: Number.parseFloat(fila.promedioGeneral) || fila.promedioGeneral || '';
							case 'Asistencias':
								return Number.isFinite(fila.asistencias)
									? fila.asistencias
									: Number.parseFloat(fila.asistencias) || fila.asistencias || '';
							case 'Inasistencias':
								return Number.isFinite(fila.inasistencias)
									? fila.inasistencias
									: Number.parseFloat(fila.inasistencias) || fila.inasistencias || '';
							case 'Observaciones':
								return fila.observaciones || '—';
							case 'Sugerencias':
								return fila.sugerencias || '—';
							default:
								return '';
						}
					}

					const valorMateria = meta.promedio
						? fila.promediosCategoria?.[meta.categoria] ?? ''
						: fila.calificaciones?.[meta.categoria]?.[meta.materia] ?? '';

					const numero = Number.parseFloat(valorMateria);
					return Number.isNaN(numero) ? valorMateria : numero;
				});

				const filaExcel = worksheet.addRow(valores);
				filaExcel.height = 24;

				columnasMeta.forEach((meta, idxCol) => {
					const celda = filaExcel.getCell(idxCol + 1);
					if (meta.tipo === 'especial') {
						const colorCell = getSpecialStyle(meta.clave, 'cell');
						if (colorCell) {
							celda.fill = crearRelleno(colorCell);
						}
						if (meta.clave === 'Nombre') {
							celda.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
						} else if (meta.clave === 'Observaciones' || meta.clave === 'Sugerencias') {
							celda.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
						} else {
							celda.alignment = { horizontal: 'center', vertical: 'middle' };
						}
					} else {
						const colorCell = getCategoryStyle(meta.categoria, 'cell');
						if (colorCell) {
							celda.fill = crearRelleno(colorCell);
						}
						celda.alignment = { horizontal: 'center', vertical: 'middle' };
					}
					celda.font = { size: 11 };
					aplicarBorde(celda);
				});
			});

			const baseNombre = descripcionGrupoSeleccionado.replace(/\s+/g, '_') || `grupo_${grupoSeleccionado}`;
			const archivo = `calificaciones_${baseNombre}_T${trimestreSeleccionado}.xlsx`;
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = archivo;
			link.click();
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
			}, 500);
		} catch (err) {
			console.error('❌ Error al exportar Excel:', err);
			alert('No fue posible generar el archivo Excel.');
		}
	}, [categorias, descripcionGrupoSeleccionado, filas, grupoSeleccionado, trimestreSeleccionado]);

	return (
		<div className="container py-4">
			<h2 className="text-center mb-4">Consulta de Calificaciones por Grupo</h2>

			<div className="row g-3 mb-4">
				<div className="col-md-5">
					<label className="form-label">Grupo</label>
					<select
						className="form-select"
						value={grupoSeleccionado}
						onChange={manejarCambioGrupo}
						disabled={loadingGrupos || loadingTabla}
					>
						<option value="">Selecciona un grupo</option>
						{grupos.map((grupo) => (
							<option key={grupo.IdGrupo} value={grupo.IdGrupo}>
								{`${grupo.Grado ?? ''}° ${grupo.CicloEscolar ? `- ${grupo.CicloEscolar}` : ''}`.trim() ||
									`Grupo ${grupo.IdGrupo}`}
							</option>
						))}
					</select>
					{loadingGrupos && <small className="text-muted">Cargando grupos...</small>}
				</div>

				<div className="col-md-3">
					<label className="form-label">Trimestre</label>
					<select
						className="form-select"
						value={trimestreSeleccionado}
						onChange={manejarCambioTrimestre}
						disabled={!grupoSeleccionado || loadingTabla}
					>
						{TRIMESTRES.map((trimestre) => (
							<option key={trimestre.valor} value={trimestre.valor}>
								{trimestre.etiqueta}
							</option>
						))}
					</select>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger" role="alert">
					<i className="fas fa-exclamation-triangle me-2"></i>
					{error}
				</div>
			)}

			{loadingTabla && (
				<div className="text-center my-4">
								<div className="spinner-border text-primary" role="status">
									<span className="visually-hidden">Cargando...</span>
					</div>
					<p className="mt-3">Cargando calificaciones del grupo seleccionado...</p>
				</div>
			)}

			{!loadingTabla && grupoSeleccionado && filas.length === 0 && (
				<div className="alert alert-info" role="alert">
					No se encontraron calificaciones para el grupo y trimestre seleccionados.
				</div>
			)}

			{!loadingTabla && filas.length > 0 && (
				<React.Fragment>
					<div
						id="reporteGrupo"
						className="rounded border p-3"
						style={{ backgroundColor: '#fffbe6' }}
					>
						<div className="d-flex justify-content-between align-items-center mb-3">
							<img src={logoISC} alt="Logo ISC" style={{ height: '60px', objectFit: 'contain' }} />
							<div className="text-center" style={{ flex: 1 }}>
								<h5 className="fw-bold mb-1">Cuadro de Calificaciones</h5>
								<p className="mb-0" style={{ fontSize: '0.85rem' }}>
									{descripcionGrupoSeleccionado || 'Grupo seleccionado'}
								</p>
								<p className="mb-0" style={{ fontSize: '0.85rem' }}>{etiquetaTrimestreSeleccionado}</p>
							</div>
							<img src={logoSEP} alt="Logo SEP" style={{ height: '40px', objectFit: 'contain' }} />
						</div>

						<div className="table-responsive">
							<table
								className="table table-bordered table-sm align-middle mb-0"
							>
								<thead>
									<tr style={{ backgroundColor: '#f2e798' }}>
										<th
											rowSpan="2"
											className="text-center"
											style={{ backgroundColor: getSpecialStyle('No', 'header') }}
										>
											No
										</th>
										<th
											rowSpan="2"
											style={{ backgroundColor: getSpecialStyle('Nombre', 'header') }}
										>
											Nombre del alumno
										</th>
										{categorias.map((categoria) => (
											<th
												key={categoria.nombre}
												className="text-center"
												colSpan={categoria.materias.length + 1}
												style={{ backgroundColor: getCategoryStyle(categoria.nombre, 'header') }}
											>
												{categoria.nombre}
											</th>
										))}
										<th
											rowSpan="2"
											className="text-center"
											style={{ backgroundColor: getSpecialStyle('PromedioGeneral', 'header') }}
										>
											Promedio General
										</th>
										<th
											rowSpan="2"
											className="text-center"
											style={{ backgroundColor: getSpecialStyle('Asistencias', 'header') }}
										>
											Asistencias
										</th>
										<th
											rowSpan="2"
											className="text-center"
											style={{ backgroundColor: getSpecialStyle('Inasistencias', 'header') }}
										>
											Inasistencias
										</th>
										<th
											rowSpan="2"
											style={{ backgroundColor: getSpecialStyle('Observaciones', 'header') }}
										>
											Observaciones
										</th>
										<th
											rowSpan="2"
											style={{ backgroundColor: getSpecialStyle('Sugerencias', 'header') }}
										>
											Sugerencias
										</th>
									</tr>
									<tr>
										{categorias.map((categoria) => (
											<React.Fragment key={`${categoria.nombre}-materias`}>
												{categoria.materias.map((materia) => (
													<th
														key={`${categoria.nombre}-${materia}`}
														className="text-center"
														style={{ backgroundColor: getCategoryStyle(categoria.nombre, 'cell') }}
													>
														{materia}
													</th>
												))}
												<th
													key={`${categoria.nombre}-promedio`}
													className="text-center"
													style={{ backgroundColor: getCategoryStyle(categoria.nombre, 'cell') }}
												>
													Promedio
												</th>
											</React.Fragment>
										))}
									</tr>
								</thead>
								<tbody>
									{filas.map((fila, index) => (
										<tr key={fila.id ?? index}>
											<td
												className="text-center"
												style={{ backgroundColor: getSpecialStyle('No', 'cell') }}
											>
												{index + 1}
											</td>
											<td style={{ backgroundColor: getSpecialStyle('Nombre', 'cell') }}>
												{fila.nombre}
											</td>
											{categorias.map((categoria) => (
												<React.Fragment key={`${fila.id}-${categoria.nombre}`}>
													{categoria.materias.map((materia) => (
														<td
															key={`${fila.id}-${categoria.nombre}-${materia}`}
															className="text-center"
															style={{ backgroundColor: getCategoryStyle(categoria.nombre, 'cell') }}
														>
															{fila.calificaciones?.[categoria.nombre]?.[materia] ?? ''}
														</td>
													))}
													<td
														key={`${fila.id}-${categoria.nombre}-promedio`}
														className="text-center fw-semibold"
														style={{ backgroundColor: getCategoryStyle(categoria.nombre, 'cell') }}
													>
														{fila.promediosCategoria?.[categoria.nombre] ?? ''}
													</td>
												</React.Fragment>
											))}
											<td
												className="text-center fw-semibold"
												style={{ backgroundColor: getSpecialStyle('PromedioGeneral', 'cell') }}
											>
												{fila.promedioGeneral}
											</td>
											<td
												className="text-center"
												style={{ backgroundColor: getSpecialStyle('Asistencias', 'cell') }}
											>
												{fila.asistencias}
											</td>
											<td
												className="text-center"
												style={{ backgroundColor: getSpecialStyle('Inasistencias', 'cell') }}
											>
												{fila.inasistencias}
											</td>
											<td style={{ whiteSpace: 'pre-wrap', backgroundColor: getSpecialStyle('Observaciones', 'cell') }}>
												{fila.observaciones || '—'}
											</td>
											<td style={{ whiteSpace: 'pre-wrap', backgroundColor: getSpecialStyle('Sugerencias', 'cell') }}>
												{fila.sugerencias || '—'}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					<div className="d-flex justify-content-center mt-3">
						<button
							type="button"
							className="btn btn-warning"
							onClick={exportarExcel}
						>
							Exportar Excel
						</button>
					</div>
				</React.Fragment>
			)}
		</div>
	);
};

export default ConsultarGruposCalificaciones;
